import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faBoxOpen,
  faUsers,
  faClipboardList,
  faSignOutAlt,
  faChartBar,
  faCommentDots,
  faTags,
  faCubes,
  faBars,
  faTimes,
  faMessage
} from '@fortawesome/free-solid-svg-icons';
import Logo from "../public/logo.jpg";
import toast, { Toaster } from 'react-hot-toast';

const Sidebar = () => {
  const [userRole, setUserRole] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUserRole(parsedUser.role);
      setUserName(parsedUser.name || parsedUser.email); // Use name if available, otherwise use email
    }
  }, []);

  const isActiveLink = (path) => {
    return location.pathname === path 
      ? "bg-[#E7F2FC] text-[#0655A2] font-semibold" 
      : "text-[#919191] hover:bg-[#E7F2FC] hover:text-[#0655A2]";
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    toast.loading('Logging out...', {
      duration: 1000,
    });

    setTimeout(() => {
      try {
        // Clear all items from localStorage
        localStorage.clear();
        
        // Show success message
        toast.success('Logged out successfully', {
          duration: 2000,
        });
        
        // Navigate to homepage after a short delay
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } catch (error) {
        toast.error('Error logging out. Please try again.', {
          duration: 3000,
        });
        console.error('Logout error:', error);
      }
    }, 1000);
  };

  return (
    <div>
      <Toaster
        position="top-right"
        toastOptions={{
          success: {
            style: {
              background: '#22c55e',
              color: 'white',
              padding: '16px',
              borderRadius: '8px',
            },
            iconTheme: {
              primary: 'white',
              secondary: '#22c55e',
            },
          },
          error: {
            style: {
              background: '#ef4444',
              color: 'white',
              padding: '16px',
              borderRadius: '8px',
            },
            iconTheme: {
              primary: 'white',
              secondary: '#ef4444',
            },
          },
          loading: {
            style: {
              background: '#0655A2',
              color: 'white',
              padding: '16px',
              borderRadius: '8px',
            },
          },
        }}
      />

      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button 
          onClick={toggleSidebar} 
          className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
          aria-label="Toggle Sidebar"
        >
          <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} className="text-2xl" />
        </button>
      </div>

      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          sidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        } lg:hidden`}
        onClick={toggleSidebar}
      />

      {/* Sidebar */}
      <nav 
        className={`h-screen w-64 bg-white shadow-xl fixed z-50 transition-transform duration-300 ease-in-out transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Logo and User Info */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-center mb-4">
            <img src={Logo} className="w-[80px] h-[80px] object-contain" alt="Company Logo" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-600">{userName}</p>
            <p className="text-xs text-gray-500 capitalize">{userRole}</p>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="overflow-y-auto h-[calc(100vh-180px)]">
          <ul className="py-2">
            {/* Field Sales Agents */}
            {userRole === 'fieldsalesagents' && (
              <>
                <li>
                  <Link to="/SalesAgentDashboard" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/SalesAgentDashboard')}`}>
                    <FontAwesomeIcon icon={faTachometerAlt} className="mr-4" />
                    <span>Dashboard</span>
                  </Link>
                </li>
                <li>
                  <Link to="/SalesCustomerDetails" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/SalesCustomerDetails')}`}>
                    <FontAwesomeIcon icon={faUsers} className="mr-4" />
                    <span>Customer List</span>
                  </Link>
                </li>
                <li>
                  <Link to="/SalesReport" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/SalesReport')}`}>
                    <FontAwesomeIcon icon={faChartBar} className="mr-4" />
                    <span>Sales Report</span>
                  </Link>
                </li>
                <li>
                  <Link to="/OrderDetails" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/OrderDetails')}`}>
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-4" />
                    <span>Orders</span>
                  </Link>
                </li>
                <li>
                  <Link to="/FeedbackLogTable" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/FeedbackLogTable')}`}>
                    <FontAwesomeIcon icon={faCommentDots} className="mr-4" />
                    <span>Feedback & Log Visits</span>
                  </Link>
                </li>
              </>
            )}

            {/* Admin */}
            {userRole === 'admin' && (
              <>
                <li>
                  <Link to="/AdminDashboard" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/AdminDashboard')}`}>
                    <FontAwesomeIcon icon={faTachometerAlt} className="mr-4" />
                    <span>Dashboard</span>
                  </Link>
                </li>
                <li>
                  <Link to="/AdminCustomerDetails" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/AdminCustomerDetails')}`}>
                    <FontAwesomeIcon icon={faUsers} className="mr-4" />
                    <span>Customer List</span>
                  </Link>
                </li>
                <li>
                  <Link to="/AdminProducts" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/AdminProducts')}`}>
                    <FontAwesomeIcon icon={faCubes} className="mr-4" />
                    <span>Products</span>
                  </Link>
                </li>
                <li>
                  <Link to="/admincompareprice" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/admincompareprice')}`}>
                    <FontAwesomeIcon icon={faClipboardList} className="mr-4" />
                    <span>Upload Compare Price</span>
                  </Link>
                </li>
                <li>
                  <Link to="/HotDealsPage" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/HotDealsPage')}`}>
                    <FontAwesomeIcon icon={faTags} className="mr-4" />
                    <span>Hot Deals</span>
                  </Link>
                </li>
                <li>
                  <Link to="/AdminOrderDetails" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/AdminOrderDetails')}`}>
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-4" />
                    <span>Orders</span>
                  </Link>
                </li>
                <li>
                  <Link to="/AdminTransactions" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/AdminTransactions')}`}>
                    <FontAwesomeIcon icon={faClipboardList} className="mr-4" />
                    <span>Transactions</span>
                  </Link>
                </li>
                <li>
                  <Link to="/AdminVisitInfo" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/AdminVisitInfo')}`}>
                    <FontAwesomeIcon icon={faClipboardList} className="mr-4" />
                    <span>Visit Info</span>
                  </Link>
                </li>
                <li>
                  <Link to="/messagepanel" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/AdminVisitInfo')}`}>
                    <FontAwesomeIcon icon={faMessage} className="mr-4" />
                    <span>Send In-App Messgae</span>
                  </Link>
                </li>
              </>
            )}

            {/* Business Manager */}
            {userRole === 'businessmanager' && (
              <>
                <li>
                  <Link to="/BusinessDashboardCards" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/BusinessDashboardCards')}`}>
                    <FontAwesomeIcon icon={faTachometerAlt} className="mr-4" />
                    <span>Dashboard</span>
                  </Link>
                </li>
                <li>
                  <Link to="/BusinessAgentPerformance" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/BusinessAgentPerformance')}`}>
                    <FontAwesomeIcon icon={faChartBar} className="mr-4" />
                    <span>Performance Metric</span>
                  </Link>
                </li>
                <li>
                  <Link to="/BusinessFeedbackTable" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/BusinessFeedbackTable')}`}>
                    <FontAwesomeIcon icon={faCommentDots} className="mr-4" />
                    <span>Feedback & Log Visits</span>
                  </Link>
                </li>
              </>
            )}

            {/* Sales Supervisors */}
            {userRole === 'salessupervisors' && (
              <>
                <li>
                  <Link to="/SalesAgentPerformance" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/SalesAgentPerformance')}`}>
                    <FontAwesomeIcon icon={faTachometerAlt} className="mr-4" />
                    <span>Dashboard</span>
                  </Link>
                </li>
                <li>
                  <Link to="/SalesSupervisorsOrderDetails" className={`flex items-center py-3 px-6 transition-colors duration-200 ${isActiveLink('/SalesSupervisorsOrderDetails')}`}>
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-4" />
                    <span>Orders</span>
                  </Link>
                </li>
              </>
            )}

            {/* Logout Button */}
            <li className="border-t border-gray-200 mt-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center py-3 px-6 text-red-500 hover:bg-red-50 transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faSignOutAlt} className="mr-4" />
                <span>Logout</span>
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;